import {
  BOARD_PADDING,
  GATE_DIAMETER,
  GRID_ROW_GAP,
  MAX_CHECKLIST_GRID_COLUMNS,
  NODE_GAP,
  NODE_HEIGHT,
  NODE_OVERLAP_SLACK,
  NODE_WIDTH,
  dimensionsForNodeKind,
  isGateNodeKind,
} from './canvasLayoutConstants'

export type CanvasSlotBox = {
  position: { x: number; y: number }
  width: number
  height: number
}

/** Same as `CanvasSlotBox` but carries the node id so displacement callers can
 * key the returned nudge map back to the underlying record. */
export type PlaceableSlot = CanvasSlotBox & { id?: string }

export type CanvasSlotNodeLike = {
  position: { x: number; y: number }
  nodeKind?: string | null
  width?: number
  height?: number
}

/**
 * Normalize any node-shaped record the caller has (a `BoardChecklistItem`, a
 * `LayoutNode`, or a bare `{position, nodeKind}`) into the AABB the collision
 * checker needs.
 */
export const toSlotBox = (node: CanvasSlotNodeLike): CanvasSlotBox => {
  if (typeof node.width === 'number' && typeof node.height === 'number') {
    return { position: node.position, width: node.width, height: node.height }
  }
  const { width, height } = dimensionsForNodeKind(node.nodeKind ?? null)
  return { position: node.position, width, height }
}

const rectsOverlap = (
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
): boolean =>
  a.x < b.x + b.width + NODE_OVERLAP_SLACK
  && a.x + a.width + NODE_OVERLAP_SLACK > b.x
  && a.y < b.y + b.height + NODE_OVERLAP_SLACK
  && a.y + a.height + NODE_OVERLAP_SLACK > b.y

const STEP_X = NODE_WIDTH + NODE_GAP
const STEP_Y = NODE_HEIGHT + GRID_ROW_GAP

// Cap on the outward spiral. 24 rings × 8 candidates ≈ 600 checks, which is
// well under the row-major fallback and still covers a stage many times the
// visible viewport.
const MAX_NEAREST_RINGS = 24

/**
 * Enumerate grid-quantized candidate positions ordered by proximity to
 * `preferred`. Anchor is the closest grid cell to preferred; we walk outward
 * one ring at a time so the first non-overlapping candidate is the closest
 * one, not the top-left of the board.
 */
const nearestCandidates = function* (
  preferred: { x: number; y: number },
): Generator<{ x: number; y: number }> {
  const anchorCol = Math.max(0, Math.round(preferred.x / STEP_X))
  const anchorRow = Math.max(0, Math.round(preferred.y / STEP_Y))
  yield { x: anchorCol * STEP_X, y: anchorRow * STEP_Y }
  for (let ring = 1; ring <= MAX_NEAREST_RINGS; ring += 1) {
    // Collect this ring's candidates, sort by Euclidean distance to preferred,
    // then yield closest-first. Ordering by ring alone can pick a cell that
    // is one ring farther but visually closer, so we sort within-ring too.
    const bucket: { x: number; y: number; d2: number }[] = []
    for (let drow = -ring; drow <= ring; drow += 1) {
      for (let dcol = -ring; dcol <= ring; dcol += 1) {
        if (Math.max(Math.abs(drow), Math.abs(dcol)) !== ring) continue
        const col = anchorCol + dcol
        const row = anchorRow + drow
        if (col < 0 || row < 0) continue
        const x = col * STEP_X
        const y = row * STEP_Y
        const dx = x - preferred.x
        const dy = y - preferred.y
        bucket.push({ x, y, d2: dx * dx + dy * dy })
      }
    }
    bucket.sort((a, b) => a.d2 - b.d2)
    for (const cell of bucket) yield { x: cell.x, y: cell.y }
  }
}

type FindEmptyCanvasSlotArgs = {
  /** Every node on the current book layout — checklists AND gates. */
  existing: CanvasSlotBox[]
  /** Bounding box of the node being placed. */
  newNode: { width: number; height: number }
  /** Optional user-supplied coordinate (drag drop, click-to-place). */
  preferred?: { x: number; y: number } | null
  /** Column count for the row-major fallback grid. */
  gridColumns?: number
}

/**
 * Return a coordinate for `newNode` that does not overlap anything in
 * `existing`. If `preferred` is given and it fits, we return it verbatim
 * (clamped to non-negative). Otherwise we walk a spiral of grid-quantized
 * cells around `preferred` and return the closest empty one — so a
 * user-driven drop lands next to their release point, never on the far
 * top-left of the board. When `preferred` is not given we fall through to
 * the historical sqrt-grid layout used by `getNextChecklistGridPosition`.
 *
 * This is the single placement authority for every add-to-canvas path —
 * manual toolbar, drag/drop, AI Line, external writer.
 */
export const findEmptyCanvasSlot = ({
  existing,
  newNode,
  preferred,
  gridColumns,
}: FindEmptyCanvasSlotArgs): { x: number; y: number } => {
  const overlaps = (candidate: { x: number; y: number }): boolean =>
    existing.some((box) =>
      rectsOverlap(
        { x: candidate.x, y: candidate.y, width: newNode.width, height: newNode.height },
        { x: box.position.x, y: box.position.y, width: box.width, height: box.height },
      ),
    )

  if (preferred) {
    const clamped = { x: Math.max(0, preferred.x), y: Math.max(0, preferred.y) }
    if (!overlaps(clamped)) return clamped
    // Spiral outward from preferred — nearest empty grid cell wins.
    for (const candidate of nearestCandidates(clamped)) {
      if (!overlaps(candidate)) return candidate
    }
    // Very unlikely to reach here; fall through to the row-major grid below.
  }

  const nonGateCount = existing.filter((box) => box.width === NODE_WIDTH).length
  const columns = gridColumns ?? Math.min(
    MAX_CHECKLIST_GRID_COLUMNS,
    Math.max(1, Math.ceil(Math.sqrt(nonGateCount + 1))),
  )

  const startY = !preferred && existing.length > 0
    ? Math.max(...existing.map((box) => box.position.y + box.height)) + NODE_GAP
    : 0

  for (let index = 0; index < 10_000; index += 1) {
    const candidate = {
      x: (index % columns) * STEP_X,
      y: startY + Math.floor(index / columns) * STEP_Y,
    }
    if (!overlaps(candidate)) return candidate
  }

  // Fallback: stack a new row well past everything we already know about.
  return { x: 0, y: startY + (nonGateCount + 1) * STEP_Y }
}

/**
 * Convenience wrapper for callers that only have a `nodeKind` string handy
 * (e.g. the drawing board's `addGateAt`, or the writer path where the item
 * is a plain `CHECKLIST`).
 */
export const findEmptyCanvasSlotForKind = (
  existing: CanvasSlotNodeLike[],
  nodeKind: string | null | undefined,
  preferred?: { x: number; y: number } | null,
): { x: number; y: number } =>
  findEmptyCanvasSlot({
    existing: existing.map(toSlotBox),
    newNode: dimensionsForNodeKind(nodeKind ?? null),
    preferred: preferred ?? null,
  })

// -----------------------------------------------------------------------------
// placeNodeNearby: nearest-slot snapping + optional gentle "make room" push.
// -----------------------------------------------------------------------------
//
// Contract: when the user releases a drag in a crowded area, we want the node
// to land as close to their release point as possible. Two strategies, tried
// in order:
//   1. If the release point clears every neighbor, keep it verbatim.
//   2. If exactly one neighbor is in the way AND the overlap is small (fits
//      within a bounded nudge budget), shift that neighbor along the shortest
//      valid axis by just enough to clear the drop. The neighbor moves AWAY
//      from the cursor, which reads as "the app made room."
//   3. Otherwise, snap the drop to the nearest empty grid cell (spiral).
//
// The push budget is intentionally small — the user's mental map of the
// board must survive the drop. Multi-neighbor pushes are deliberately not
// attempted; they cascade in ways that feel like the layout is fighting
// the user.

const PUSH_MAX_TRAVEL = NODE_GAP * 2 // 88px — hard cap on any single nudge.

type PushDirection = { dx: number; dy: number; distance: number }

const computePushCandidates = (
  neighbor: CanvasSlotBox,
  dropped: { x: number; y: number; width: number; height: number },
): PushDirection[] => {
  // Distance the neighbor must travel in each direction to fully clear the
  // dropped node (including overlap slack). Negative shifts mean move
  // left/up; positive means right/down.
  const shiftRight = (dropped.x + dropped.width + NODE_OVERLAP_SLACK) - neighbor.position.x
  const shiftLeft = (neighbor.position.x + neighbor.width + NODE_OVERLAP_SLACK) - dropped.x
  const shiftDown = (dropped.y + dropped.height + NODE_OVERLAP_SLACK) - neighbor.position.y
  const shiftUp = (neighbor.position.y + neighbor.height + NODE_OVERLAP_SLACK) - dropped.y
  return [
    { dx: shiftRight, dy: 0, distance: shiftRight },
    { dx: -shiftLeft, dy: 0, distance: shiftLeft },
    { dx: 0, dy: shiftDown, distance: shiftDown },
    { dx: 0, dy: -shiftUp, distance: shiftUp },
  ]
}

type PlaceNodeNearbyArgs = {
  existing: PlaceableSlot[]
  newNode: { width: number; height: number }
  preferred: { x: number; y: number }
  /** When true, one neighbor may be gently nudged to make room. */
  allowDisplacement?: boolean
}

export type PlaceNodeNearbyResult = {
  position: { x: number; y: number }
  displacements: Map<string, { x: number; y: number }>
}

export const placeNodeNearby = ({
  existing,
  newNode,
  preferred,
  allowDisplacement = false,
}: PlaceNodeNearbyArgs): PlaceNodeNearbyResult => {
  const clamped = { x: Math.max(0, preferred.x), y: Math.max(0, preferred.y) }
  const droppedBox = { x: clamped.x, y: clamped.y, width: newNode.width, height: newNode.height }

  const overlappingNeighbors = existing.filter((box) =>
    rectsOverlap(droppedBox, {
      x: box.position.x, y: box.position.y, width: box.width, height: box.height,
    }),
  )

  if (overlappingNeighbors.length === 0) {
    return { position: clamped, displacements: new Map() }
  }

  // Push-to-fit: only when a single neighbor is in the way. Multi-neighbor
  // cascades are the thing that makes canvas apps feel adversarial, so we
  // skip that path and fall through to nearest-slot snapping instead.
  if (allowDisplacement && overlappingNeighbors.length === 1) {
    const neighbor = overlappingNeighbors[0]
    const candidates = computePushCandidates(neighbor, droppedBox)
      // Only push forward — a negative "distance" means the two boxes were
      // already separated on that axis, which means the split has already
      // happened elsewhere; ignore.
      .filter((push) => push.distance > 0 && push.distance <= PUSH_MAX_TRAVEL)
      // Shortest push feels most natural.
      .sort((a, b) => a.distance - b.distance)

    for (const push of candidates) {
      const nextPos = {
        x: neighbor.position.x + push.dx,
        y: neighbor.position.y + push.dy,
      }
      if (nextPos.x < 0 || nextPos.y < 0) continue
      // The pushed neighbor must not land on top of any OTHER existing box
      // (nor on the dropped node itself).
      const pushedBox = { x: nextPos.x, y: nextPos.y, width: neighbor.width, height: neighbor.height }
      if (rectsOverlap(pushedBox, droppedBox)) continue
      const collidesAfterPush = existing.some((other) => {
        if (other === neighbor) return false
        return rectsOverlap(pushedBox, {
          x: other.position.x, y: other.position.y, width: other.width, height: other.height,
        })
      })
      if (collidesAfterPush) continue

      const displacements = new Map<string, { x: number; y: number }>()
      if (neighbor.id) displacements.set(neighbor.id, nextPos)
      return { position: clamped, displacements }
    }
  }

  // Fall through: snap to the closest empty grid cell.
  const boxesForFallback: CanvasSlotBox[] = existing.map((box) => ({
    position: box.position, width: box.width, height: box.height,
  }))
  const snapped = findEmptyCanvasSlot({
    existing: boxesForFallback,
    newNode,
    preferred: clamped,
  })
  return { position: snapped, displacements: new Map() }
}

export const canvasLayoutSizes = {
  NODE_WIDTH,
  NODE_HEIGHT,
  GATE_DIAMETER,
  NODE_GAP,
  GRID_ROW_GAP,
  BOARD_PADDING,
  MAX_CHECKLIST_GRID_COLUMNS,
  isGateNodeKind,
} as const
